import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Divider,
  Paper,
  Chip,
  InputAdornment,
  TextField,
  IconButton,
} from '@mui/material';
import { Receipt, Calculator } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { formatCurrency, getCurrencySymbol } from '@lib/formatters';
import { getIntlLocale } from '@lib/locale';
import client from '@api/client';
import { ORDER_API } from '../api/endpoints';
import useAuthStore from '../stores/authStore';
import useOrderStore from '../stores/orderStore';
import { storage } from '../utils/storage';
import { getErrorMessage } from '../lib/errors';

const OrderSummaryDialog = () => {
  const { t } = useTranslation('orders');
  const open = useOrderStore((s) => s.orderDialogOpen);
  const orderSummary = useOrderStore((s) => s.orderSummary);
  const setOrderDialogOpen = useOrderStore((s) => s.setOrderDialogOpen);
  const onClose = () => setOrderDialogOpen(false);
  // ===== Hooks immer zuerst =====
  const [tables, setTables] = useState([]);
  const [cashOpen, setCashOpen] = useState(false);
  const [givenStr, setGivenStr] = useState(''); // Eingabe als String (für Komma/Backspace)
  const [due, setDue] = useState(0); // Zu zahlender Betrag beim Öffnen
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [restaurantId, setRestaurantId] = useState(null);
  const [role, setRole] = useState(null);
  const [restaurantName, setRestaurantName] = useState(null);

  const presets = [5, 10, 20, 50, 100];

  useEffect(() => {
    const authState = useAuthStore.getState();
    const storedRestaurantId = authState.restaurantId;
    const restaurantName = authState.restaurantName;
    const storedRole = authState.user?.role;

    if (storedRestaurantId && storedRole && restaurantName) {
      setRestaurantName(restaurantName);
      setRestaurantId(storedRestaurantId);
      setRole(storedRole);
    } else {
    }
  }, []);

  // ===== Helpers: robustes Zahlen-Handling =====
  const toNumber = (v) => {
    if (typeof v === 'number') return v;
    const n = parseFloat(String(v ?? 0).replace(',', '.'));
    return Number.isNaN(n) ? 0 : n;
  };
  const fmt2 = (v) => toNumber(v).toFixed(2);

  // ===== Rechner-Logik =====
  const openCash = () => {
    const raw = orderSummary?.total ?? orderSummary?.subtotal ?? 0;
    const amount = Math.round(toNumber(raw) * 100) / 100; // sauber auf 2 Nachkommastellen
    setDue(amount);
    setGivenStr('');
    setCashOpen(true);
  };
  const closeCash = () => setCashOpen(false);

  const given = toNumber(givenStr || 0);
  const change = Math.max(0, Number((given - due).toFixed(2)));

  const appendChar = (ch) => {
    setGivenStr((prev) => {
      let s = prev || '';
      if (ch === ',') ch = '.';
      if (ch === '.' && s.includes('.')) return s;
      if (!/^[0-9.]*$/.test(ch)) return s;
      const next = s + ch;
      if (!/^\d*\.?\d{0,2}$/.test(next)) return s; // max. 2 Nachkommastellen
      return next;
    });
  };
  const backspace = () =>
    setGivenStr((prev) => (prev ? prev.slice(0, -1) : ''));
  const clearAll = () => setGivenStr('');
  const addPreset = (val) => {
    setGivenStr((prev) => {
      const current = toNumber(prev || 0);
      return (current + val).toFixed(2);
    });
  };
  const handleExact = () => setGivenStr(due.toFixed(2));

  // ===== Guard NACH den Hooks =====
  if (!orderSummary) return null;

  const totalDisplay = fmt2(orderSummary?.total ?? orderSummary?.subtotal ?? 0);
  const qr = storage.getQR(); // QR-Status holen

  const handleBestellungabschließen = async () => {
    if (!orderSummary?.orderId) {
      return;
    }

    try {
      const response = await client.patch(
        `${ORDER_API}/${orderSummary.orderId}/status`,
        { status: 'COMPLETED' },
      );

      if (response.status === 200) {
        // Snackbar anzeigen
        setSnackbar({
          open: true,
          message: t('orderSummary.completedSuccess', 'Bestellung wurde erfolgreich abgeschlossen.'),
          severity: 'success',
        });

        onClose();

      } else {
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: getErrorMessage(error, t('orderSummary.completedError', 'Fehler beim Abschließen der Bestellung.')),
        severity: 'error',
      });
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        sx={{ zIndex: 1300 }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            fontWeight: 'bold',
            fontSize: 22,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <Receipt size={24} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          {t('orderSummary.title', 'Bestellübersicht')}
          {/* Calculator icon only when not in QR mode */}
          {qr !== 'ja' && (
            <IconButton
              onClick={openCash}
              sx={{ position: 'absolute', right: 12, color: 'primary.contrastText' }}
              aria-label={t('orderSummary.openCalculator', 'Rückgeldrechner öffnen')}
            >
              <Calculator size={24} />
            </IconButton>
          )}
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: 'background.default', px: 3 }}>
          <Typography>
            <strong>{t('orderDetails.orderNumber', 'Bestellnummer')}:</strong> {orderSummary.orderNumber}
          </Typography>
          <Typography>
            <strong>{t('orderDetails.notes', 'Anmerkungen')}:</strong>{' '}
            {orderSummary.specialInstructions ?? '—'}
          </Typography>
          {orderSummary.tax != null && (
            <Typography>
              <strong>{t('orderDetails.tax', 'Steuer')}:</strong> {formatCurrency(orderSummary.tax)}
            </Typography>
          )}
          <Typography>
            <strong>{t('orderDetails.total', 'Gesamtbetrag')}:</strong> {formatCurrency(orderSummary?.total ?? orderSummary?.subtotal ?? 0)}
          </Typography>
          <Typography>
            <strong>{t('orderDetails.placedAt', 'Bestellt am')}:</strong>{' '}
            {new Date(orderSummary.placedAt).toLocaleString(getIntlLocale(), {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </Typography>

          <Box mt={2}>
            {orderSummary?.items?.length > 0 &&
              orderSummary.items.map((item, index) => (
                <Paper
                  key={`${item.name}-${index}`}
                  elevation={3}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    mt: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="h6" color="primary.main" fontWeight="bold">
                      {item.quantity}× {item.name}
                    </Typography>
                    <Typography fontWeight="bold" fontSize={16} color="primary.main">
                      {formatCurrency(item.total)}
                    </Typography>
                  </Box>

                  {item.notes && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t('orderDetails.specialInstructions', 'Hinweise')}: {item.notes}
                    </Typography>
                  )}

                  {item.components?.length > 0 && (
                    <Box mt={1}>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        {t('orderSummary.selectedOptions', 'Ausgewählte Optionen')}:
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, m: 0 }}>
                        {item.components.map((comp, i) => (
                          <li key={comp.optionName || i} style={{ marginBottom: '4px' }}>
                            <Typography variant="body2">
                              🥗 {comp.value} –{' '}
                              <span style={{ color: 'var(--mui-palette-text-secondary)' }}>
                                {comp.optionName}
                              </span>
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          {role === 'MANAGER' && (
            <Button
              onClick={openCash}
              variant="outlined"
              fullWidth
              startIcon={<Calculator size={20} />}
            >
              {t('orderSummary.calculator', 'Rückgeldrechner')}
            </Button>
          )}
          {role === 'MANAGER' && (
            <Button
              onClick={handleBestellungabschließen}
              variant="contained"
              color="primary"
              fullWidth
            >
              {t('orderSummary.complete', 'Erledigt')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ===== Taschenrechner-Dialog ===== */}
      <Dialog open={cashOpen} onClose={closeCash} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>{t('orderSummary.cashPayment', 'Barzahlung')}</DialogTitle>
        <DialogContent dividers>
          {/* Zu zahlen */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', mb: 1 }}>
            <Typography>{t('orderSummary.toPay', 'Zu zahlen')}</Typography>
            <Typography sx={{ fontWeight: 800 }}>{formatCurrency(due)}</Typography>
          </Box>

          {/* Presets & Genau */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {presets.map((v) => (
              <Chip
                key={v}
                label={`${v} ${getCurrencySymbol()}`}
                onClick={() => addPreset(v)}
                variant="outlined"
              />
            ))}
            <Chip
              label={`+0,10 ${getCurrencySymbol()}`}
              onClick={() => addPreset(0.1)}
              variant="outlined"
            />
            <Chip label={t('orderSummary.exact', 'Genau')} color="primary" onClick={handleExact} />
          </Box>

          {/* Manuelle Eingabe */}
          <TextField
            label={t('orderSummary.amountGiven', 'Betrag gegeben')}
            fullWidth
            type="text"
            value={givenStr}
            onChange={(e) => {
              const v = e.target.value.replace(',', '.');
              if (/^\d*\.?\d{0,2}$/.test(v) || v === '') setGivenStr(v);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">{getCurrencySymbol()}</InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Numerisches Keypad */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1,
            }}
          >
            {['7', '8', '9', '4', '5', '6', '1', '2', '3'].map((k) => (
              <Button
                key={k}
                variant="outlined"
                onClick={() => appendChar(k)}
                sx={{ py: 1.5, fontSize: '1.25rem', fontWeight: 700 }}
              >
                {k}
              </Button>
            ))}
            <Button
              variant="outlined"
              color="error"
              onClick={clearAll}
              sx={{ py: 1.5, fontWeight: 700 }}
            >
              C
            </Button>
            <Button
              variant="outlined"
              onClick={() => appendChar('0')}
              sx={{ py: 1.5, fontSize: '1.25rem', fontWeight: 700 }}
            >
              0
            </Button>
            <Button
              variant="outlined"
              onClick={() => appendChar(',')}
              sx={{ py: 1.5, fontSize: '1.25rem', fontWeight: 700 }}
            >
              ,
            </Button>
            <Button
              variant="outlined"
              onClick={backspace}
              sx={{ gridColumn: 'span 3', py: 1.5, fontWeight: 700 }}
            >
              ←
            </Button>
          </Box>

          {/* Rückgeld */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {t('orderSummary.change', 'Rückgeld')}: {formatCurrency(change)}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeCash}>{t('orderSummary.close', 'Schließen')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderSummaryDialog;
